import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Environment, EnvironmentService } from '@galaxy/core';
import { LocationInterface } from '@vendasta/snapshot';
import { differenceInHours } from 'date-fns';
import { Subject, firstValueFrom } from 'rxjs';
import { SnapshotSummary, TrackingProperties } from './scorecard';
import { ScorecardService } from './scorecard.service';

@Component({
  selector: 'snap-scorecard',
  templateUrl: './scorecard.component.html',
  styleUrls: ['./scorecard.component.scss'],
})
export class ScorecardComponent {
  private readonly businessId$$ = new Subject<string>();
  @Input() set businessId(businessId: string) {
    if (businessId) {
      this.businessId$$.next(businessId);
    }
  }

  private readonly partnerId$$ = new Subject<string>();
  @Input() set partnerId(partnerId: string) {
    if (partnerId) {
      this.partnerId$$.next(partnerId);
    }
  }

  @Input() snapshotName: string;
  @Input() trackingCategory: string;
  @Input() enableURLCopy = false;
  @Input() showEditButton = false;
  @Input() openInEditMode = false;

  @Output() refreshReportClickedEvent: EventEmitter<void> = new EventEmitter<void>();

  readonly snapshotUrl: Promise<string>;
  readonly snapshotSummary: Promise<SnapshotSummary>;
  readonly trackingProperties: Promise<TrackingProperties>;
  readonly address: Promise<string>;
  readonly isLessThan24HoursOld: Promise<boolean>;
  readonly snapshotEditV2URL: Promise<string>;

  constructor(
    private service: ScorecardService,
    private environmentService: EnvironmentService,
  ) {
    const currentSnapshot = firstValueFrom(this.businessId$$).then((businessId) =>
      firstValueFrom(this.service.getCurrentSnapshot(businessId)),
    );

    this.snapshotSummary = currentSnapshot.then((current) =>
      firstValueFrom(this.service.getSummary(current?.snapshotId)),
    );

    this.trackingProperties = this.snapshotSummary.then((summary) => {
      return {
        snapshotId: summary?.snapshotId || '',
        category: this.trackingCategory || '',
      };
    });

    this.address = this.snapshotSummary.then((summary) => buildAddress(summary?.location));
    this.isLessThan24HoursOld = this.snapshotSummary.then((s) => differenceInHours(new Date(), s.created) < 24);

    this.snapshotUrl = Promise.all([this.getSnapshotDomain(), currentSnapshot]).then(([domain, snapshot]) => {
      if (!snapshot.path) {
        return '';
      }

      // let host = window.location.hostname;
      if (window.location.hostname === 'localhost') {
        // host = 'localhost:' + window.location.port;
      }

      return domain + snapshot.path;
    });

    this.snapshotEditV2URL = Promise.all([this.getSnapshotDomain(), currentSnapshot]).then(([domain, snapshot]) => {
      if (!snapshot.snapshotId) {
        return '';
      }

      const snapshotID = snapshot.snapshotId;
      return `${domain}/snapshot/v2/${snapshotID}/edit`;
    });
  }

  private async getSnapshotDomain(): Promise<string> {
    return firstValueFrom(this.partnerId$$)
      .then((partnerId) => firstValueFrom(this.service.getDomain(partnerId)))
      .then((d) => {
        const scheme = d.secure ? 'https' : 'http';
        if (d.domain) {
          return `${scheme}://${d.domain}`;
        }
        return this.host;
      });
  }

  onViewSnapshotClicked(snapshotURL: string): void {
    this.openSnapshotUrl(snapshotURL, this.openInEditMode);
  }

  onEditSnapshotClicked(): void {
    this.openSnapshotV2EditUrl();
  }

  onRefreshSnapshotClicked(): void {
    this.refreshReportClickedEvent.emit();
  }

  openSnapshotUrl(snapshotURL: string, isEditMode: boolean): void {
    if (isEditMode) {
      snapshotURL = snapshotURL + '/edit';
    }
    window.open(snapshotURL, '_blank', 'noopener');
  }

  async openSnapshotV2EditUrl(): Promise<void> {
    await this.snapshotEditV2URL.then((url) => window.open(url, '_blank', 'noopener'));
  }

  private get host(): string {
    switch (this.environmentService.getEnvironment()) {
      case Environment.DEMO:
        return `https://salestool-demo.appspot.com`;
      case Environment.PROD:
        return `https://www.snapshotreport.biz`;
      default:
        return `https://www.snapshotreport.biz`;
    }
  }
}

export function buildAddress(location: LocationInterface): string {
  if (!location) {
    return '';
  }
  let address = location.address || '';
  if (location.city) {
    address += ` ${location.city}`;
  }
  if (location.state) {
    address += `, ${location.state}`;
  }
  if (location.country) {
    address += `, ${location.country}`;
  }
  return address;
}
